/**
 * «Cómo está», leído de un parte YA GUARDADO, para enseñarlo en su ficha.
 *
 * Al clasificar, `ConfirmSamplingFields` enseña lo que dijo el trabajador (D19),
 * pero una vez confirmado desaparecía: la ficha del muestreo no lo decía en
 * ningún sitio (Pablo, 24-09-2026). Esto lo lee de las mismas columnas que
 * escribe `followUpColumns`, sin volver a preguntar nada.
 *
 * El «No lo sé» a «¿Cómo está ahora?» NO se guarda en `evolution_verdict`
 * (su CHECK solo admite mejor/igual/peor/ya no hay). Como esa pregunta no se
 * puede pasar sin contestar —ni en el asistente del trabajador ni en la
 * revisión pedida—, «es lo mismo» sin avance guardado y sin nadie que lo
 * cambiara después (`evolution_verdict_by` vacío) solo puede ser ese «No lo sé».
 */

export type ReportVerdict = "better" | "same" | "worse" | "gone" | "unknown";
export type ReportAnswer = "same" | "different" | "unknown";
export type ReportSkipReason = "timeout" | "error" | "offline";

export type SessionReport =
  /** Contestó a «¿Es lo mismo que ves ahora?» (y, si era lo mismo, a «¿Cómo está?»). */
  | { kind: "answer"; answer: ReportAnswer; verdict: ReportVerdict | null; changedBy: string | null }
  /** Sin pregunta (lo muestreó un admin, o «Añadir actualización»), pero con avance. */
  | { kind: "verdict"; verdict: Exclude<ReportVerdict, "unknown">; changedBy: string | null }
  /** Había algo que preguntar y no se pudo. */
  | { kind: "not_asked"; reason: ReportSkipReason };

export interface SessionReportRow {
  inspector_id?: string | null;
  followup_answer?: string | null;
  followup_skip_reason?: string | null;
  evolution_verdict?: string | null;
  evolution_verdict_by?: string | null;
}

const ANSWERS: readonly string[] = ["same", "different", "unknown"];
const VERDICTS: readonly string[] = ["better", "same", "worse", "gone"];
const SKIPS: readonly string[] = ["timeout", "error", "offline"];

export function sessionReportOf(row: SessionReportRow | null | undefined): SessionReport | null {
  if (!row) return null;
  const verdict = row.evolution_verdict && VERDICTS.includes(row.evolution_verdict)
    ? (row.evolution_verdict as Exclude<ReportVerdict, "unknown">)
    : null;
  // Quien lo puso no es quien muestreó: lo cambió alguien después (4.8, «Editar gravedad y avance»).
  const changedBy =
    verdict && row.evolution_verdict_by && row.inspector_id && row.evolution_verdict_by !== row.inspector_id
      ? row.evolution_verdict_by
      : null;

  if (row.followup_answer && ANSWERS.includes(row.followup_answer)) {
    const answer = row.followup_answer as ReportAnswer;
    const said: ReportVerdict | null =
      verdict ?? (answer === "same" && !row.evolution_verdict_by ? "unknown" : null);
    return { kind: "answer", answer, verdict: said, changedBy };
  }
  if (verdict) return { kind: "verdict", verdict, changedBy };
  if (row.followup_skip_reason && SKIPS.includes(row.followup_skip_reason)) {
    return { kind: "not_asked", reason: row.followup_skip_reason as ReportSkipReason };
  }
  return null;
}

/** El avance que se pinta, si lo hay (el mismo ▲▬▼ que el mapa y la ficha del foco). */
export function reportVerdict(r: SessionReport | null): ReportVerdict | null {
  if (!r || r.kind === "not_asked") return null;
  return r.verdict;
}

/**
 * El avance DICHO de un muestreo, para el mapa, el histórico y la ficha del foco.
 * «No lo sé» sale como `"unknown"` y NO se rellena comparando gravedades: un
 * «Igual» sacado de la gravedad sería decir algo que el trabajador no dijo
 * (Pablo, 24-09-2026: «no podemos poner que está igual»).
 */
export function saidVerdict(row: SessionReportRow | null | undefined): ReportVerdict | null {
  return reportVerdict(sessionReportOf(row));
}
